import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Plus,
  X,
  Loader2,
  Eye,
  CheckCircle,
  PenLine,
  User,
  UserCheck,
  Users,
  Shield,
} from 'lucide-react';

// Workflow status colors
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' },
  in_review: { label: 'In Review', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' },
  approved: { label: 'Approved', color: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' },
  rejected: { label: 'Rejected', color: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' },
  archived: { label: 'Archived', color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' },
  superseded: { label: 'Superseded', color: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300' },
  filed: { label: 'Filed', color: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' }
};

/**
 * WorkflowModal - Workflow actions and assignee management
 */
const WorkflowModal = ({
  isOpen,
  onClose,
  selectedDocForWorkflow,
  workflowAssignees,
  setWorkflowAssignees,
  workflowComment,
  setWorkflowComment,
  processingWorkflow,
  handleWorkflowAction,
  addAssignee,
  removeAssignee,
  updateAssignee,
  getWorkflowActions,
  getSignatureStatus,
}) => {
  const handleClose = (open) => {
    onClose(open);
    if (!open) {
      setWorkflowComment('');
      setWorkflowAssignees([{ email: '', role: 'reviewer', signature_required: false }]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-slate-100">Workflow Actions</DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Current status: <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedDocForWorkflow?.status]?.color}`}>
              {STATUS_CONFIG[selectedDocForWorkflow?.status]?.label}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Current Signature Status - 21 CFR Part 11 */}
          {selectedDocForWorkflow?.assignees_with_roles?.length > 0 && (
            <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-blue-300 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Assigned Personnel & Signature Status
                </Label>
                {(() => {
                  const status = getSignatureStatus(selectedDocForWorkflow);
                  return status.required > 0 ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-500/30 text-blue-300 border border-red-500/30">
                      {status.signed} / {status.required} Signatures
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-600 dark:text-slate-400">
                      No signatures required
                    </span>
                  );
                })()}
              </div>
              <div className="space-y-2">
                {selectedDocForWorkflow.assignees_with_roles.map((assignee, i) => {
                  const hasSigned = selectedDocForWorkflow.signatures?.some(
                    sig => sig.signer_email?.toLowerCase() === assignee.email?.toLowerCase()
                  );
                  const signature = selectedDocForWorkflow.signatures?.find(
                    sig => sig.signer_email?.toLowerCase() === assignee.email?.toLowerCase()
                  );
                  const sigRequired = assignee.signature_required === true;
                  
                  return (
                    <div key={i} className={`p-3 rounded-lg border ${
                      hasSigned ? 'bg-green-500/20 border-green-500/30' : 
                      sigRequired ? 'bg-amber-500/20 border-amber-500/30' : 
                      'bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            hasSigned ? 'bg-green-500/30' : sigRequired ? 'bg-amber-500/30' : 'bg-slate-200'
                          }`}>
                            {hasSigned ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : sigRequired ? (
                              <PenLine className="w-4 h-4 text-amber-600" />
                            ) : (
                              <User className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{assignee.email}</p>
                            <div className="flex items-center gap-1">
                              <Badge className={`text-[10px] uppercase ${
                                assignee.role === 'reviewer' ? 'bg-red-500/30 text-blue-300' :
                                assignee.role === 'approver' ? 'bg-purple-500/30 text-purple-300' :
                                'bg-amber-500/30 text-amber-300'
                              }`}>
                                {assignee.role}
                              </Badge>
                              {sigRequired && (
                                <Badge className="text-[10px] bg-red-500/30 text-red-300">
                                  Sig Required
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`text-xs font-medium ${
                          hasSigned ? 'text-green-600' : 
                          sigRequired ? 'text-amber-600' : 
                          'text-slate-500 dark:text-slate-400'
                        }`}>
                          {hasSigned ? '✓ Signed' : sigRequired ? '⏳ Pending Signature' : 'No signature needed'}
                        </span>
                      </div>
                      {hasSigned && signature && (
                        <div className="mt-2 pt-2 border-t border-green-500/30 text-xs">
                          <p className="text-green-300 italic">{signature.signature_text}</p>
                          <p className="text-green-600 mt-1">
                            {signature.signature_meaning}
                          </p>
                          <p className="text-green-500 mt-1">
                            Signed: {new Date(signature.signed_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Assign New Reviewers / Approvers */}
          <div className="space-y-3 p-4 bg-slate-100 dark:bg-slate-800/80 rounded-lg border border-slate-300 dark:border-slate-600">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Users className="w-4 h-4" />
                Assign Reviewers / Approvers / QC
              </Label>
              <Button variant="outline" size="sm" onClick={addAssignee} className="h-7 bg-slate-200 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
            
            {workflowAssignees.map((assignee, index) => (
              <div key={index} className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Email address of assignee"
                      value={assignee.email}
                      onChange={(e) => updateAssignee(index, 'email', e.target.value)}
                      className="bg-slate-200 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
                    />
                    <div className="flex gap-2">
                      <Select
                        value={assignee.role}
                        onValueChange={(value) => updateAssignee(index, 'role', value)}
                      >
                        <SelectTrigger className="flex-1 bg-slate-200 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                          <SelectItem value="reviewer" className="text-gray-700 hover:bg-slate-200">
                            <span className="flex items-center gap-2">
                              <Eye className="w-3 h-3 text-red-600" /> Reviewer
                            </span>
                          </SelectItem>
                          <SelectItem value="approver" className="text-gray-700 hover:bg-slate-200">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-purple-600" /> Approver
                            </span>
                          </SelectItem>
                          <SelectItem value="qc" className="text-gray-700 hover:bg-slate-200">
                            <span className="flex items-center gap-2">
                              <UserCheck className="w-3 h-3 text-amber-600" /> QC
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Signature Required Toggle */}
                    <div className="flex items-center justify-between p-2 bg-red-500/20 rounded border border-red-500/30">
                      <div className="flex items-center gap-2">
                        <PenLine className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-blue-300">Signature Required?</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant={assignee.signature_required ? "default" : "outline"}
                          size="sm"
                          className={`h-7 px-3 ${assignee.signature_required ? 'bg-green-600 hover:bg-green-700 text-slate-900 dark:text-slate-100' : 'bg-slate-200 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200'}`}
                          onClick={() => updateAssignee(index, 'signature_required', true)}
                        >
                          Yes
                        </Button>
                        <Button
                          type="button"
                          variant={!assignee.signature_required ? "default" : "outline"}
                          size="sm"
                          className={`h-7 px-3 ${!assignee.signature_required ? 'bg-gray-300 hover:bg-gray-400 text-slate-900 dark:text-slate-100' : 'bg-slate-200 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200'}`}
                          onClick={() => updateAssignee(index, 'signature_required', false)}
                        >
                          No
                        </Button>
                      </div>
                    </div>
                  </div>
                  {workflowAssignees.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeAssignee(index)} className="mt-1 text-red-600 hover:text-red-300 hover:bg-red-500/20">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Only assignees with &quot;Signature Required = Yes&quot; will be prompted to provide electronic signatures per 21 CFR Part 11
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 dark:text-slate-300">Comment (optional)</Label>
            <Input
              placeholder="Add a comment for the workflow history"
              value={workflowComment}
              onChange={(e) => setWorkflowComment(e.target.value)}
              className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
            />
          </div>

          <div className="space-y-2 p-4 bg-slate-100 dark:bg-slate-800/80 rounded-lg border border-slate-300 dark:border-slate-600">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Available Actions</Label>
            {getWorkflowActions(selectedDocForWorkflow?.status).map(({ action, label, icon: Icon }) => (
              <Button
                key={action}
                variant="outline"
                className="w-full justify-start bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-gray-700 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100"
                onClick={() => handleWorkflowAction(action)}
                disabled={processingWorkflow}
              >
                {processingWorkflow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2" />}
                {label}
              </Button>
            ))}
            {getWorkflowActions(selectedDocForWorkflow?.status).length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No actions available for this status</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowModal;
